import { Injectable } from '@angular/core';

/**
 * Service to handle Android back button for modals.
 * Modals register themselves when opened and unregister when closed.
 * The goBackHistory() function checks this service before navigating.
 */
@Injectable({ providedIn: 'root' })
export class ModalBackHandlerService {
  private modalStack = new Map<string, () => void>();

  /**
   * Register a modal when it opens.
   * @param id Unique identifier for the modal
   * @param closeCallback Function to call to close the modal
   */
  register(id: string, closeCallback: () => void): void {
    this.modalStack.set(id, closeCallback);
  }

  /**
   * Unregister a modal when it closes.
   * @param id Unique identifier for the modal
   */
  unregister(id: string): void {
    this.modalStack.delete(id);
  }

  /**
   * Check if there are any open modals.
   */
  hasOpenModal(): boolean {
    return this.modalStack.size > 0;
  }

  /**
   * Close the topmost modal (LIFO).
   * Map maintains insertion order, so we get the last entry.
   * @returns true if a modal was closed, false if no modal was open
   */
  closeTopModal(): boolean {
    if (this.modalStack.size > 0) {
      const entries = Array.from(this.modalStack.entries());
      const [id, closeCallback] = entries[entries.length - 1];
      this.modalStack.delete(id);
      closeCallback();
      return true;
    }
    return false;
  }

  /**
   * Get the number of open modals (for debugging).
   */
  getOpenModalCount(): number {
    return this.modalStack.size;
  }
}
